import {
  FetchGraphQLError,
  type GraphQLErrorItem,
  type GraphQLResponse,
  getLocalizedGraphQLError,
  normalizeGraphQLErrors,
  pickDisplayGraphQLError,
  reportGraphQLErrorsToRum,
} from './graphql-error-helpers';

type ProcessGraphQLResponseParams<TResult> = Readonly<{
  payload: GraphQLResponse<TResult>;
  source: string;
  operationName?: string;
  clientRequestId?: string;
  variables?: unknown;
  opType?: string;
  httpStatus?: string;
}>;

export async function processGraphQLResponse<TResult>({
  payload,
  source,
  operationName,
  clientRequestId,
  variables,
  opType,
  httpStatus,
}: ProcessGraphQLResponseParams<TResult>): Promise<TResult> {
  if (payload.errors?.length) {
    const normalizedErrors = normalizeGraphQLErrors(payload.errors);
    const displayError = pickDisplayGraphQLError(normalizedErrors);

    reportGraphQLErrorsToRum({
      source,
      errors: normalizedErrors,
      operationName,
      clientRequestId,
      variables,
      opType,
      httpStatus,
      rawErrorData: payload.errors,
    });

    if (!displayError) {
      throw new FetchGraphQLError({
        message: 'Unknown GraphQL error',
      });
    }

    const localizedError = await getLocalizedGraphQLError({
      error: displayError,
    });

    throw new FetchGraphQLError({
      message: localizedError.message,
      title: localizedError.title,
      code: displayError.code,
      statusCode: displayError.statusCode,
      isSystemGraphQLError: displayError.isSystemGraphQLError,
    });
  }

  if (!payload.data) {
    throw new FetchGraphQLError({
      message: 'GraphQL response has no data',
    });
  }

  return payload.data;
}

export function toGraphQLResponse(payload: unknown): GraphQLResponse<unknown> {
  if (typeof payload !== 'object' || payload === null) {
    return {};
  }

  const maybePayload = payload as {
    data?: unknown;
    errors?: readonly GraphQLErrorItem[];
  };

  return {
    data: maybePayload.data,
    errors: maybePayload.errors,
  };
}
